import React, { useState, useEffect } from 'react';
import { Typography, Box, Button, IconButton, Grid } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ICompany } from '../../../redux/company/types';
import { updateCompany } from '../../../redux/company/actions';
import { setmedia } from '../../../redux/project/actions';
import { X, Play } from 'lucide-react';
import { UserProjectSearchDialogue } from '../../UserProjectSearchDialogue/UserProjectSearchDialogue';
import { IProject } from '../../../types/global/helper';
import { COMPANY_BRAND_ORANGE } from '../../../constants/companyBrand';

interface ICompanyVideosEdit {
    company?: ICompany;
    isEditing: boolean;
    onSave: (saveFunction: () => void) => void;
    onChanges: (hasChanges: boolean) => void;
}

const SURFACE_CARD = '#1a1a1a';
const BTN_SURFACE = '#262626';

const CompanyVideosEdit: React.FC<ICompanyVideosEdit> = ({ company, isEditing, onSave, onChanges }) => {
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({
        company_videos: company?.company_videos || [],
    });
    const [initialData, setInitialData] = useState(formData);
    const [isProjectSearchOpen, setIsProjectSearchOpen] = useState(false);

    useEffect(() => {
        if (company) {
            const newData = {
                company_videos: company.company_videos || [],
            };
            setFormData(newData);
            setInitialData(newData);
        }
    }, [company]);

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialData);
        onChanges(hasChanges);
    }, [formData, initialData, onChanges]);

    const handleSave = () => {
        if (company?._id) {
            const payload: { id: string; body: { company_videos: { _id: string }[] } } = {
                id: company._id,
                body: {
                    company_videos: formData.company_videos.map(vid => ({
                        _id: vid._id,
                    })),
                },
            };
            dispatch(updateCompany(payload));
        }
    };

    useEffect(() => {
        onSave(handleSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSave, formData, company?._id]);

    const handleAddVideos = (selectedProjects: IProject[]) => {
        setFormData(prev => ({
            ...prev,
            company_videos: [...prev.company_videos, ...selectedProjects],
        }));
        setIsProjectSearchOpen(false);
    };

    const handleRemoveVideo = (videoId: string) => {
        setFormData(prev => ({
            ...prev,
            company_videos: prev.company_videos.filter(video => video._id !== videoId),
        }));
    };

    const handlePlayVideo = (video: IProject) => {
        dispatch(
            setmedia({
                media: video,
                mediaType: 'video',
            }),
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { md: 'flex-end' },
                    justifyContent: 'space-between',
                    gap: 3,
                }}>
                <Box>
                    <Typography
                        component="h1"
                        sx={{
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            fontWeight: 800,
                            fontSize: { xs: '1.75rem', md: '2.25rem' },
                            color: '#ffffff',
                            letterSpacing: '-0.02em',
                            mb: 1.5,
                        }}>
                        Company{' '}
                        <Box component="span" sx={{ color: COMPANY_BRAND_ORANGE }}>
                            Videos
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.55)',
                            maxWidth: 520,
                            lineHeight: 1.65,
                            fontSize: '0.95rem',
                            fontFamily: FONT_ENUM.MEDIUM_TEXT,
                        }}>
                        Showcase reels and projects that represent your company on your public profile.
                    </Typography>
                </Box>
                {isEditing && (
                    <Button
                        startIcon={
                            <Add
                                sx={{
                                    color: COMPANY_BRAND_ORANGE,
                                    fontSize: 18,
                                    transition: 'transform 0.25s ease',
                                }}
                            />
                        }
                        onClick={() => setIsProjectSearchOpen(true)}
                        sx={{
                            alignSelf: { xs: 'flex-start', md: 'auto' },
                            flexShrink: 0,
                            display: 'inline-flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: BTN_SURFACE,
                            color: '#ffffff',
                            borderRadius: 999,
                            px: 2.5,
                            py: 0.75,
                            minHeight: 40,
                            textTransform: 'none',
                            fontSize: 12,
                            fontWeight: 700,
                            fontFamily: FONT_ENUM.BOLD_HEADER,
                            letterSpacing: '0.04em',
                            whiteSpace: 'nowrap',
                            lineHeight: 1,
                            '& .MuiButton-startIcon': {
                                marginRight: 0,
                                marginLeft: 0,
                            },
                            '&:hover': {
                                bgcolor: '#333333',
                                '& .MuiButton-startIcon .MuiSvgIcon-root': { transform: 'rotate(90deg)' },
                            },
                        }}>
                        ADD VIDEOS
                    </Button>
                )}
            </Box>

            <Box
                sx={{
                    bgcolor: SURFACE_CARD,
                    borderRadius: 3,
                    p: { xs: 2, sm: 3 },
                    border: '1px solid rgba(255,255,255,0.06)',
                }}>
                {formData.company_videos.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'rgba(255,255,255,0.55)' }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                            No videos added yet. Click &quot;Add Videos&quot; to get started.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {formData.company_videos.map(video => (
                            <Grid item xs={12} sm={6} md={4} key={video._id}>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        height: 200,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        backgroundColor: '#0a0a0a',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            borderColor: COMPANY_BRAND_ORANGE,
                                        },
                                    }}>
                                    {isEditing && (
                                        <IconButton
                                            onClick={() => handleRemoveVideo(video._id)}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                backgroundColor: 'rgba(0,0,0,0.75)',
                                                color: '#ffffff',
                                                width: 32,
                                                height: 32,
                                                zIndex: 3,
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,115,81,0.9)',
                                                },
                                            }}>
                                            <X size={16} />
                                        </IconButton>
                                    )}

                                    <Box
                                        onClick={() => handlePlayVideo(video)}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            backgroundImage: `url(${video.thumbnail || video.feature_image})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                        }}>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                backgroundColor: 'rgba(0, 0, 0, 0.35)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'background-color 0.2s ease-in-out',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.55)',
                                                },
                                            }}>
                                            <Box
                                                sx={{
                                                    width: 50,
                                                    height: 50,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        backgroundColor: 'white',
                                                        transform: 'scale(1.08)',
                                                    },
                                                }}>
                                                <Play size={20} color="#111" fill="#111" />
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            right: 0,
                                            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.85))',
                                            p: 2,
                                        }}>
                                        <Typography
                                            sx={{
                                                color: '#ffffff',
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                                lineHeight: 1.2,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                            }}>
                                            {video.title}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            <UserProjectSearchDialogue
                open={isProjectSearchOpen}
                onClose={() => setIsProjectSearchOpen(false)}
                type="video"
                maxSelection={10}
                onSubmit={handleAddVideos}
                surfaceVariant="companyProfile"
            />
        </Box>
    );
};

export { CompanyVideosEdit };
